import { useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { lookupIoc, lookupCoOccurringIocs } from '../../lib/graphApi';
import type { IocLookupResult, CoOccurringIocItem } from '../../lib/graphApi';
import { severityColor, getExternalLinks, formatCategoryTitle } from './iocDetailHelpers';
import EntityDetailShell from '../entity/EntityDetailShell';
import EntityHeader, { type HeaderAction } from '../entity/EntityHeader';
import FactsBand, { type FactsStat } from '../entity/FactsBand';
import DocumentsTable, { type DocumentRow } from '../entity/DocumentsTable';
import CoOccurrenceTable, { type CoOccurrenceRow } from '../entity/CoOccurrenceTable';
import { sparklineFromDocs } from '../entity/sparklineFromDocs';
import { categoryColor, ENTITY_COLORS } from '../../lib/entityColors';
import { iocDetailPath } from '../../lib/paths';
import ExpandableChipRow from '../../components/ExpandableChipRow';

export default function IocDetailPage() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const iocValue = params['*'] || '';
  const iocType = searchParams.get('type') || undefined;

  // Reject malformed URLs like /ioc/_ — anything shorter than the minimum
  // valid IOC length (matches normalizer._passes_universal_gate). Skips the
  // API call entirely and renders the not-found state.
  const isValidLookupValue = iocValue.length >= 3;

  const { data, isLoading } = useQuery<IocLookupResult>({
    queryKey: ['ioc-detail', iocValue, iocType],
    queryFn: () => lookupIoc(iocValue, iocType),
    enabled: isValidLookupValue,
  });

  const { data: coOccurring } = useQuery<CoOccurringIocItem[]>({
    queryKey: ['ioc-cooccurring', iocValue],
    queryFn: () => lookupCoOccurringIocs(iocValue),
    enabled: isValidLookupValue,
  });

  const resolvedType = data?.ioc_type || iocType || '';
  const externalLinks = getExternalLinks(resolvedType, iocValue);
  const categoryTitle = formatCategoryTitle(data?.category, resolvedType, iocValue);
  const stats = data?.mention_stats;
  const cve = data?.cve_details;
  const hasCveData = !!cve && (cve.cvss_score != null || cve.description);
  const entities = data?.entities ?? [];

  const documents: DocumentRow[] = useMemo(
    () => (data?.documents ?? []).map((d) => ({ ...d })),
    [data?.documents],
  );

  const coRows: CoOccurrenceRow[] = useMemo(() => {
    if (!coOccurring) return [];
    return coOccurring.map((item) => ({
      id: `${item.ioc_type}::${item.ioc_value}`,
      type: item.ioc_type.replace(/_/g, ' '),
      value: item.ioc_value,
      href: iocDetailPath(item.ioc_value, item.ioc_type.toLowerCase()),
      count: item.co_occurrence_count,
    }));
  }, [coOccurring]);

  if (isLoading) {
    return (
      <div className="ioc-detail-page">
        <button className="ioc-detail-back" onClick={() => navigate(-1)}>&larr; Back</button>
        <div className="ioc-detail-loading">Scanning indicator</div>
      </div>
    );
  }

  const actions: HeaderAction[] = externalLinks.map((link) => ({
    key: link.name,
    label: link.name,
    href: link.url,
    external: true,
  }));

  const facts_stats: FactsStat[] = [];
  if (stats?.doc_count) facts_stats.push({ label: 'reports', value: stats.doc_count });
  if (stats?.actor_count) facts_stats.push({ label: 'actors', value: stats.actor_count });
  if (stats?.family_count) facts_stats.push({ label: 'families', value: stats.family_count });
  if (cve?.cvss_score != null) facts_stats.push({ label: 'CVSS', value: cve.cvss_score });

  const badges = (entities.length > 0 || hasCveData) ? (
    <>
      {hasCveData && cve?.cvss_score != null && (
        <span className="ioc-detail-cvss-badge" style={{ backgroundColor: severityColor(cve.cvss_severity) }}>
          CVSS {cve.cvss_score}{cve.cvss_severity && ` (${cve.cvss_severity})`}
        </span>
      )}
      {hasCveData && cve?.in_cisa_kev && <span className="ioc-detail-kev-badge">CISA KEV</span>}
      {hasCveData && cve?.cwe_ids && <span className="ioc-detail-meta-tag">{cve.cwe_ids}</span>}
      {hasCveData && cve?.published_date && <span className="ioc-detail-meta-tag">Published {cve.published_date}</span>}
    </>
  ) : null;

  const attribution = entities.length > 0 ? (
    <ExpandableChipRow
      items={entities.map((entity) => (
        <span key={`${entity.label}-${entity.name}`} className={`ioc-detail-entity-tag entity-tag-${entity.label.toLowerCase()}`}>
          {entity.label === 'MalwareFamily' ? 'Family' : 'Actor'}:&nbsp;
          {entity.malpedia_url ? (
            <a href={entity.malpedia_url} target="_blank" rel="noreferrer">{entity.name}</a>
          ) : entity.name}
        </span>
      ))}
      toggleClassName="ioc-detail-entity-tag ioc-detail-entity-tag-toggle"
    />
  ) : null;

  const borderColor = data?.category ? categoryColor(data.category.key) : ENTITY_COLORS.hash;

  return (
    <EntityDetailShell
      header={
        <EntityHeader
          badge={resolvedType.replace(/_/g, ' ') || 'IOC'}
          badgeClassName={`ioc-${resolvedType.toLowerCase()}`}
          category={categoryTitle}
          value={iocValue}
          copyValue={iocValue}
          actions={actions}
          metaStrip={badges}
        />
      }
      facts={
        (stats || badges || attribution) ? (
          <FactsBand
            badges={null}
            stats={facts_stats}
            attribution={attribution}
            sparkline={{ firstSeen: stats?.first_seen, lastSeen: stats?.last_seen, series: sparklineFromDocs(documents) }}
            borderColor={borderColor}
          />
        ) : null
      }
      description={hasCveData && cve?.description ? <p className="ioc-detail-description">{cve.description}</p> : null}
      notFound={data && !data.found ? (
        <div className="ioc-detail-not-found">
          Not indexed in knowledge graph.{externalLinks.length > 0 && ' Check external resources above for more information.'}
        </div>
      ) : null}
      documents={documents.length > 0 ? (
        <>
          <h3 className="ioc-detail-section-title">Referenced in {documents.length} document{documents.length !== 1 ? 's' : ''}</h3>
          <DocumentsTable documents={documents} />
        </>
      ) : null}
      cooccurrence={coRows.length > 0 ? (
        <>
          <h3 className="ioc-detail-section-title">Co-occurring IOCs</h3>
          <CoOccurrenceTable rows={coRows} typeFacetLabel="Type" />
        </>
      ) : null}
    />
  );
}
